import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import Slider from '@mui/material/Slider';
import { DomHelper } from '@src/services/helpers/dom';
import Hls from 'hls.js';
import { style, classes, stylesheet } from 'typestyle';
import { DisplayableContent } from '@src/types/Media';
import { Dimensions } from '@src/types/Dimensions';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Column } from '../Utils';
import { VideoTime } from '../Common/VideoTime';
import type { SubtitleTrack } from '@src/queries/media/api';
import { SubtitleControl } from './SubtitleControl';
import {
  mapSubtitleTracks, nativeLanguageName, shouldConvertSubtitleToVtt, srtToVtt,
} from './subtitles';
import { getContainedVideoRect, getSubtitleCaptionFontSize } from './videoGeometry';
import type { VideoRect } from './videoGeometry';
import {
  buildCaptionPages, getCaptionPageIndex, getCaptionViewportHeight,
} from './captionPagination';
import type { CaptionCueTiming, CaptionLineRect, CaptionPage } from './captionPagination';

const INSTAGRAM_IFRAME_HEADER = 54;

interface IProps {
  media: DisplayableContent
  url?: string
  subtitles?: SubtitleTrack[]
}

const EMPTY_SUBTITLE_TRACKS: SubtitleTrack[] = [];

interface ActiveCaptionCue extends CaptionCueTiming {
  key: string;
  text: string;
}

function isM3U8(videoUrl: string): boolean {
  try {
    return new URL(videoUrl, document.baseURI).pathname.split('.').pop() === 'm3u8';
  } catch {
    return false;
  }
}

export function VideoPlayer({ media, url, subtitles }: IProps) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [hidebar, setHidebar] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [containerHeight, setContainerHeight] = useState(1);
  const [containerWidth, setContainerWidth] = useState(1);
  const [videoPicture, setVideoPicture] = useState<VideoRect>({ left: 0, top: 0, width: 1, height: 1 });
  const [sliderVal, setSliderVal] = useState(0);
  const [selectedSubtitle, setSelectedSubtitle] = useState('off');
  const [subtitleSources, setSubtitleSources] = useState<Record<number, string>>({});
  const [captionCues, setCaptionCues] = useState<ActiveCaptionCue[]>([]);
  const [captionTime, setCaptionTime] = useState(0);
  const [captionPagesByCue, setCaptionPagesByCue] = useState<Record<string, CaptionPage[]>>({});

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);
  const captionTextRefs = useRef(new Map<string, HTMLDivElement>());
  const barRef = useRef<HTMLDivElement>(null);
  const videoIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const hideBarMoveRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const seekTargetRef = useRef<number | null>(null);
  const seekStartedAtRef = useRef(0);
  const nativeSeekingRef = useRef(false);
  const captionTimeRef = useRef(0);

  const usableSubtitles = useMemo(
    () => mapSubtitleTracks(subtitles ?? EMPTY_SUBTITLE_TRACKS),
    [subtitles],
  );
  const activeSubtitleIndex = selectedSubtitle === 'off' ? null : Number(selectedSubtitle);
  const captionFontSize = getSubtitleCaptionFontSize(videoPicture.width, videoPicture.height);
  const captionMaxHeight = getCaptionViewportHeight(videoPicture.height, captionFontSize);
  const captionPageBudget = Math.max(1, (captionMaxHeight - 4) / Math.max(1, captionCues.length));
  const captionVisibleHeight = captionCues.reduce((height, cue) => {
    const pages = captionPagesByCue[cue.key] || [{ offset: 0, contentHeight: captionPageBudget }];
    const pageIndex = getCaptionPageIndex([cue], captionTime, pages.length);
    return height + (pages[pageIndex]?.contentHeight || captionPageBudget);
  }, 4);

  useLayoutEffect(() => {
    if (!captionCues.length || !captionRef.current) {
      setCaptionPagesByCue((current) => (Object.keys(current).length === 0 ? current : {}));
      return;
    }

    const pageBudget = captionPageBudget;
    const pagesByCue = captionCues.reduce<Record<string, CaptionPage[]>>((result, cue) => {
      const content = captionTextRefs.current.get(cue.key);
      if (!content) return result;
      const range = document.createRange();
      range.selectNodeContents(content);
      const contentTop = content.getBoundingClientRect().top;
      const renderedRects = typeof range.getClientRects === 'function' ? range.getClientRects() : [];
      const lineRects: CaptionLineRect[] = Array.from(renderedRects).map((rect) => ({
        top: rect.top - contentTop,
        bottom: rect.bottom - contentTop,
      }));
      result[cue.key] = lineRects.length > 0
        ? buildCaptionPages(lineRects, pageBudget)
        : [{ offset: 0, contentHeight: pageBudget }];
      return result;
    }, {});
    setCaptionPagesByCue((current) => {
      const sameKeys = Object.keys(current).length === Object.keys(pagesByCue).length;
      const samePages = sameKeys && Object.entries(pagesByCue).every(([key, pages]) => (
        current[key]?.length === pages.length
        && pages.every((page, index) => (
          current[key][index].offset === page.offset
          && current[key][index].contentHeight === page.contentHeight
        ))
      ));
      return samePages ? current : pagesByCue;
    });
  }, [captionCues, captionFontSize, videoPicture.width, videoPicture.height, captionPageBudget]);

  useEffect(() => {
    setError(false);
    setLoaded(false);
    setPlaying(false);
  }, [url, media.video]);

  useEffect(() => {
    setSelectedSubtitle('off');
    setSubtitleSources({});
    setCaptionCues([]);
    setCaptionTime(0);
    setCaptionPagesByCue({});
    seekTargetRef.current = null;
    seekStartedAtRef.current = 0;
    nativeSeekingRef.current = false;
    captionTimeRef.current = 0;
  }, [url, media.video]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onSeeking = () => { nativeSeekingRef.current = true; };
    const onSeeked = () => {
      nativeSeekingRef.current = false;
      const target = seekTargetRef.current;
      if (target !== null && Math.abs(video.currentTime - target) < 0.5) {
        seekTargetRef.current = null;
        seekStartedAtRef.current = 0;
      }
    };
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    return () => {
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [url, media.video]);

  useEffect(() => {
    if (activeSubtitleIndex !== null && !usableSubtitles[activeSubtitleIndex]) {
      setSelectedSubtitle('off');
      setSubtitleSources({});
      setCaptionCues([]);
    }
  }, [activeSubtitleIndex, usableSubtitles]);

  useEffect(() => {
    const index = Number(selectedSubtitle);
    const track = usableSubtitles[index];
    if (selectedSubtitle === 'off' || !track) return undefined;
    const controller = new AbortController();
    let blobUrl: string | undefined;
    fetch(track.src, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const vtt = shouldConvertSubtitleToVtt(track.src, track.format, text) ? srtToVtt(text) : text;
        blobUrl = URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' }));
        if (!controller.signal.aborted) setSubtitleSources({ [index]: blobUrl });
        else URL.revokeObjectURL(blobUrl);
      })
      .catch(() => undefined); // Subtitle loading is optional and must not affect video playback.

    return () => {
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [selectedSubtitle, usableSubtitles]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const tracks = video.textTracks;
    for (let index = 0; index < tracks.length; index += 1) tracks[index].mode = 'disabled';
    const selectedIndex = Number(selectedSubtitle);
    const selectedTrack = selectedSubtitle === 'off' ? undefined : Array.from(tracks).find(
      (track) => track.id === `media-subtitle-${selectedIndex}`,
    );
    if (selectedTrack) selectedTrack.mode = 'hidden';
    setCaptionCues([]);
    if (!selectedTrack) return undefined;
    const updateCaption = () => {
      const cues = selectedTrack.activeCues;
      if (!cues?.length) {
        setCaptionCues([]);
        captionTimeRef.current = video.currentTime;
        setCaptionTime(video.currentTime);
        return;
      }
      const activeCues: ActiveCaptionCue[] = [];
      for (let index = 0; index < cues.length; index += 1) {
        const cue = cues[index] as TextTrackCue & { text?: string };
        const text = cue.text?.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').trim();
        if (text) activeCues.push({
          key: `${cue.startTime}:${cue.endTime}:${index}:${text}`,
          text,
          startTime: cue.startTime,
          endTime: cue.endTime,
        });
      }
      setCaptionCues(activeCues);
      captionTimeRef.current = video.currentTime;
      setCaptionTime(video.currentTime);
    };
    const updateCaptionTime = () => {
      captionTimeRef.current = video.currentTime;
      setCaptionTime(video.currentTime);
    };
    selectedTrack.addEventListener('cuechange', updateCaption);
    video.addEventListener('timeupdate', updateCaptionTime);
    video.addEventListener('seeking', updateCaptionTime);
    video.addEventListener('seeked', updateCaptionTime);
    video.addEventListener('pause', updateCaptionTime);
    updateCaption();
    return () => {
      selectedTrack.removeEventListener('cuechange', updateCaption);
      video.removeEventListener('timeupdate', updateCaptionTime);
      video.removeEventListener('seeking', updateCaptionTime);
      video.removeEventListener('seeked', updateCaptionTime);
      video.removeEventListener('pause', updateCaptionTime);
    };
  }, [media.video, selectedSubtitle, subtitleSources]);

  // Shadow refs for use inside timers/callbacks to avoid stale closures
  const loadedRef = useRef(false);
  const playingRef = useRef(false);
  loadedRef.current = loaded;
  playingRef.current = playing;

  useEffect(() => {
    const videoUrl = url || media.video;
    const video = videoRef.current;
    if (!video || !videoUrl || !isM3U8(videoUrl)) return undefined;
    const hls = new Hls();
    hls.attachMedia(video);
    hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(videoUrl));
    return () => hls.destroy();
  }, [url, media.video]);

  useEffect(() => {
    videoIntervalRef.current = setInterval(() => {
      if (!videoRef.current) {
        return;
      }
      const video = videoRef.current;
      const { currentTime: ct, duration: dur } = video;
      const pendingTarget = seekTargetRef.current;
      let seekTimedOut = false;
      if (pendingTarget !== null) {
        const reachedTarget = !video.seeking && !nativeSeekingRef.current
          && Math.abs(ct - pendingTarget) < 0.5;
        const timedOut = Date.now() - seekStartedAtRef.current > 3000;
        if (reachedTarget || timedOut) {
          seekTargetRef.current = null;
          seekStartedAtRef.current = 0;
          seekTimedOut = timedOut;
        } else {
          return;
        }
      }

      if ((video.seeking || nativeSeekingRef.current) && !seekTimedOut) return;
      if (Math.abs(ct - captionTimeRef.current) >= 0.1) {
        captionTimeRef.current = ct;
        setCaptionTime(ct);
      }

      if (loadedRef.current && Number.isFinite(dur) && dur > 0 && Number.isFinite(ct)) {
        setSliderVal(Math.max(0, Math.min(100, (ct / dur) * 100)));
        setDuration(Math.floor(dur));
        setCurrentTime(Math.floor(ct));
      }
    }, 1000 / 24);

    onMouseMove();

    return () => {
      if (videoIntervalRef.current) {
        clearInterval(videoIntervalRef.current);
      }
      if (hideBarMoveRef.current) {
        clearTimeout(hideBarMoveRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const video = videoRef.current;
    const videoUrl = url || media.video;
    let hasCurrentMetadata = false;
    if (video && videoUrl && video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      try {
        hasCurrentMetadata = video.currentSrc === new URL(videoUrl, document.baseURI).href;
      } catch {
        hasCurrentMetadata = false;
      }
    }

    const updateLayout = () => {
      const containerBounds = container.getBoundingClientRect();
      setContainerWidth(container.clientWidth);
      setContainerHeight(container.clientHeight);
      const videoBounds = video?.getBoundingClientRect() || containerBounds;
      setVideoPicture(getContainedVideoRect(
        containerBounds,
        videoBounds,
        hasCurrentMetadata ? video?.videoWidth || 0 : 0,
        hasCurrentMetadata ? video?.videoHeight || 0 : 0,
      ));
    };
    const resetPictureToPlayerBounds = () => {
      const bounds = container.getBoundingClientRect();
      setVideoPicture({
        left: 0,
        top: 0,
        width: container.clientWidth || bounds.width || 1,
        height: container.clientHeight || bounds.height || 1,
      });
    };
    const handleResize = () => updateLayout();
    const handleLoadedMetadata = () => {
      hasCurrentMetadata = true;
      updateLayout();
    };

    resetPictureToPlayerBounds();
    updateLayout();
    window.addEventListener('resize', handleResize);
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(handleResize) : undefined;
    resizeObserver?.observe(container);
    if (video) {
      resizeObserver?.observe(video);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      video?.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video?.removeEventListener('resize', handleResize);
    };
  }, [url, media.video]);

  function updateVideoPictureDimensions() {
    const container = containerRef.current;
    if (!container) return;
    const containerBounds = container.getBoundingClientRect();
    const video = videoRef.current;
    const videoBounds = video?.getBoundingClientRect() || containerBounds;
    setVideoPicture(getContainedVideoRect(
      containerBounds,
      videoBounds,
      video?.videoWidth || 0,
      video?.videoHeight || 0,
    ));
  }

  function onPlayButton(isPlaying: boolean) {
    if (!videoRef.current) {
      return;
    }
    setPlaying(isPlaying);
    if (!isPlaying) {
      setHidebar(false);
    }
    if (isPlaying) {
      void videoRef.current.play().catch(() => setPlaying(false));
    } else {
      videoRef.current.pause();
    }
  }

  function setVideoTime(val: number) {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const target = video.duration * (val / 100);
    if (!Number.isFinite(target)) return;
    seekTargetRef.current = target;
    seekStartedAtRef.current = Date.now();
    video.currentTime = target;
  }

  function onChangeTime(e: Event, val: number | number[]): void {
    const newSliderVal = Array.isArray(val) ? val[0] : val;
    if (!Number.isFinite(newSliderVal)) return;
    const boundedSliderVal = Math.max(0, Math.min(100, newSliderVal));
    setSliderVal(boundedSliderVal);
    setVideoTime(boundedSliderVal);
  }

  function onEnd() {
    onPlayButton(false);
  }

  function onError() {
    onPlayButton(false);
    setError(true);
  }

  function onLoaded() {
    updateVideoPictureDimensions();
    setLoaded(true);
    onPlayButton(true);
  }

  function onMouseMove() {
    if (hideBarMoveRef.current) {
      clearTimeout(hideBarMoveRef.current);
    }
    setHidebar(false);
    hideBarMoveRef.current = setTimeout(() => {
      if (playingRef.current && DomHelper.elementIsHovered(barRef.current)) {
        setHidebar(true);
      }
    }, 2000);
  }

  function onMouseLeave() {
    if (playingRef.current) {
      setHidebar(true);
    }
  }

  function instagramHack(
    containerDimensions: Dimensions,
    videoDimensions?: Dimensions,
    isVertical?: boolean,
    isReel?: boolean,
  ) {
    if (!videoDimensions || containerDimensions.height === -1) {
      return null;
    }
    const containerRatio = containerDimensions.width / containerDimensions.height;
    const videoRatio = videoDimensions.width / videoDimensions.height;

    let size: Dimensions | null;

    if (videoRatio > containerRatio) {
      size = {
        width: containerDimensions.width,
        height: containerDimensions.width / videoRatio,
      };
    } else {
      size = {
        width: containerDimensions.height * videoRatio,
        height: containerDimensions.height,
      };
    }

    const instagramEmbed = `https://www.instagram.com/` + (isReel ? `reel` : `p`) + `/${media.instagramHack}/embed/captioned/`;

    if (!size) {
      return null;
    }
    const videoStyle = {
      width: size.width,
      height: size.height,
    };

    const transformStyle = videoRatio < 1 ? { transform: 'scale(1.5) translateY(78px)' } : { transform: 'translateY(-35px) scale(1.1)' };

    return (
      <Column className={instagramIframeContainerClass}>
        <Column
          className={instagramIframeSubContainerClass}
          style={isVertical ? { ...videoStyle, ...transformStyle } : videoStyle}
        >
          <iframe
            id="instagram-embed-0"
            data-instgrm-payload-id="instagram-media-payload-0"
            allowFullScreen
            className={classes(['instagram-media instagram-media-rendered', instagramIframeClass])}
            scrolling="no"
            style={{
              position: 'absolute',
              width: '100%',
              height: size.height + INSTAGRAM_IFRAME_HEADER - 2,
              top: -INSTAGRAM_IFRAME_HEADER,
            }}
            src={instagramEmbed}
            frameBorder={0}
          />
        </Column>
      </Column>
    );
  }

  const { image_large: imageURL, dimensions } = media;
  return media
    ? (
    <div
      className={sheet.videoPlayer}
      ref={containerRef}
      data-testid="video-player"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      data-loaded={loaded}
      data-error={error}
      style={{
        background: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${imageURL})`,
        backgroundRepeat: 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {media.instagramHack ? (
        instagramHack({
          height: containerHeight,
          width: containerWidth,
        }, dimensions, media.isVertical, media.isReel)
      ) : (
        <>
          <div
            className={classes(sheet.playPause, {
              [sheet.noMouse]: hidebar,
            })}
            onClick={() => onPlayButton(!playing)}
          >
            {!playing ? (
            <PlayArrowRoundedIcon
              className={playIconClass}
              style={{ fontSize: '6rem' }}
            />
            ) : null}
          </div>
          {loaded ? (
          <div className={sheet.bar} data-hide={hidebar.toString()} ref={barRef}>
            <div className={sheet.progression}>
              <Slider
                className={sheet.slider}
                aria-labelledby="continuous-slider"
                value={sliderVal}
                onChange={onChangeTime}
              />
            </div>
            <div className={sheet.time}>
              <VideoTime value={currentTime} />
              <span className={sheet.separator}>/</span>
              <VideoTime value={duration} />
            </div>
          </div>
          ) : null}
          {usableSubtitles.length > 0 ? (
            <div className={sheet.subtitleControl}>
              <SubtitleControl
                tracks={usableSubtitles}
                audioLang={media.audioLang}
                activeTrackIndex={activeSubtitleIndex}
                onSelect={(index) => setSelectedSubtitle(index === null ? 'off' : String(index))}
              />
            </div>
          ) : null}
          {media
            ? (
            <video
              ref={videoRef}
              onEnded={onEnd}
              onLoadedMetadata={onLoaded}
              src={url || media.video}
              style={{ objectFit: 'contain' }}
              onError={onError}
            >
              {usableSubtitles.map((track, index) => {
                const src = subtitleSources[index];
                return src ? (
                  <track
                    key={`${track.srclang}-${track.src}`}
                    id={`media-subtitle-${index}`}
                    kind="subtitles"
                    srcLang={track.srclang}
                    label={nativeLanguageName(track.srclang)}
                    src={src}
                  />
                ) : null;
              })}
            </video>
            ) : null}
          {captionCues.length > 0 && activeSubtitleIndex !== null && usableSubtitles[activeSubtitleIndex] ? (
            <div
              className={sheet.subtitlePicture}
              data-testid="subtitle-picture"
              style={{
                left: videoPicture.left,
                top: videoPicture.top,
                width: videoPicture.width,
                height: videoPicture.height,
              }}
            >
              <div
                ref={captionRef}
                className={sheet.subtitleCaption}
                data-testid="subtitle-caption"
                style={{
                  height: `${captionVisibleHeight}px`,
                  fontSize: `${captionFontSize}px`,
                }}
                aria-live="off"
              >
                {captionCues.map((cue) => {
                  const pages = captionPagesByCue[cue.key]
                    || [{ offset: 0, contentHeight: captionPageBudget }];
                  const pageIndex = getCaptionPageIndex([cue], captionTime, pages.length);
                  const page = pages[pageIndex] || pages[0];
                  return (
                    <div
                      key={cue.key}
                      className={sheet.subtitleCueViewport}
                      data-page-index={pageIndex}
                      style={{ height: `${page.contentHeight}px` }}
                    >
                      <div
                        ref={(node) => {
                          if (node) captionTextRefs.current.set(cue.key, node);
                          else captionTextRefs.current.delete(cue.key);
                        }}
                        className={sheet.subtitleCaptionText}
                        data-testid="subtitle-caption-text"
                        style={{ transform: `translateY(-${page.offset}px)` }}
                      >
                        {cue.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div className="error">
            {window.T('collect.media.fullscreen.videoerror')}
          </div>
        </>
      )}
    </div>
    ) : null;
}

const instagramIframeContainerClass = style({
  height: '100%',
  width: '100%',
  justifyContent: 'center',
});

const instagramIframeSubContainerClass = style({
  position: 'relative',
  overflow: 'hidden',
  flexGrow: 0,
  margin: 'auto',
});

const instagramIframeClass = style({
  width: '100%',
  position: 'absolute',
});

const playIconClass = style({
  margin: 'auto',
});

const sheet = (() => {
  const $height = 20;
  const $size = 100;
  const $padding = 12;
  const $colorButtons = '#3a76ff';
  return stylesheet({
    videoPlayer: {
      position: 'relative',
      overflow: 'hidden',
      height: '100%',
      width: '100%',
      $nest: {
        video: {
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        },
        '.error': {
          display: 'none',
          padding: '80px 0',
        },
        '&[data-error="true"], &[data-loaded="false"]': {
          overflow: 'visible',
          $nest: {
            '&video, &.play-pause': {
              display: 'none',
            },
          },
        },
        '&[data-error="true"] .error': {
          display: 'block',
        },
      },
    },
    slider: {
      marginTop: -3,
    },
    playPause: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 2,
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      textAlign: 'center',
      fontSize: '3.4rem',
      cursor: 'pointer',
      $nest: {
      },
    },
    noMouse: {
      cursor: 'none',
    },
    bar: {
      overflow: 'hidden',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      zIndex: 3,
      height: $height,
      backgroundColor: 'rgba(0, 0, 0, 0.35)',
      transition: 'bottom 0.6s',
      $nest: {
        '&[data-hide="true"]': {
          bottom: -$height,
        },
      },
    },
    subtitleControl: {
      position: 'absolute',
      top: 16,
      right: 16,
      zIndex: 1002,
    },
    subtitlePicture: {
      position: 'absolute',
      zIndex: 12,
      overflow: 'hidden',
      pointerEvents: 'none',
    },
    subtitleCaption: {
      position: 'absolute',
      left: '3%',
      right: '3%',
      bottom: '12%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      padding: '2px 12px',
      overflow: 'hidden',
      borderRadius: 4,
      background: 'rgba(0, 0, 0, 0.72)',
    },
    subtitleCueViewport: {
      position: 'relative',
      width: '100%',
      flex: '0 0 auto',
      overflow: 'hidden',
    },
    subtitleCaptionText: {
      display: 'block',
      color: '#fff',
      fontFamily: 'var(--adalong-subtitle-font-family, "Fustat", Arial, sans-serif)',
      fontSize: 'inherit',
      fontWeight: 600,
      lineHeight: 1.3,
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-line',
    },
    progression: {
      height: '100%',
      paddingRight: $size + $padding,
      paddingLeft: $padding,
      $nest: {
        '.MuiSlider-rail': {
          backgroundColor: 'rgba(233, 233, 233, 0.23)',
        },
        '.MuiSlider-track': {
          color: $colorButtons,
        },
        '.MuiSlider-thumb': {
          color: '#e9e9e9',
        },
      },
    },
    time: {
      width: $size,
      height: '100%',
      position: 'absolute',
      right: 0,
      top: 0,
      fontSize: 13,
      padding: '1px 8px',
      color: '#dfdfdf',
      letterSpacing: 0,
    },
    separator: {
      margin: 3,
      fontSize: 10,
      verticalAlign: 'text-bottom',
    },
  });
})();
